// Dice scene layout. Reuses the generic classification + geometry from
// @aerofruits/game-shell and adds the Dice-specific region allocation for the
// three compositions:
//   unified  -> a fixed virtual stage (STAGE) contain-fit into the viewport
//               (desktop + tablet-landscape).
//   portrait -> compact full-screen reflow stacked top->bottom in the safe rect
//               (phones + tablets in portrait).
//   rail     -> landscape phones: play column on the left + control rail right.
// Everything here is pure geometry and unit-tested; no PixiJS imports.
import {
  classifyLayout,
  compositionKind,
  safeRect,
  dprCap as shellDprCap,
  reducedEffects as shellReducedEffects,
  type CompositionKind,
  type Insets,
  type LayoutMode,
  type Rect,
  type Viewport,
} from '@aerofruits/game-shell';

export type { Viewport, Insets, Rect, LayoutMode, CompositionKind };
export { fitRect, NO_INSETS } from '@aerofruits/game-shell';

/** Fixed virtual stage for the unified composition (16:10). */
export const STAGE = { width: 1280, height: 800 } as const;

/** Minimum reserved heights for DOM overlays and readable sections (virtual px). */
export const LAYOUT_MIN = {
  resultBlock: { unified: 108, portrait: 36, rail: 56 },
  dice: { unified: 96, portrait: 84, rail: 80 },
  /** Single number row (selection + Lightning display). */
  selector: { unified: 96, portrait: 56, rail: 64 },
  errorToast: 36,
  balanceRow: { portrait: 36 },
  betLastWinRow: { portrait: 36 },
  betControlsRow: { portrait: 44 },
  rollRow: { portrait: 48 },
  bottomInset: { portrait: 8 },
} as const;

export const RESULT_FONT_MIN = 13;
export const LIGHTNING_MULT_FONT_MIN = 11;
export const INPUT_FONT_MIN = 16;
export const TOUCH_TARGET_MIN = 44;

/** Pure height for the compact single-line result between dice and selector. */
export function resultBlockHeight(kind: CompositionKind, containerH: number): number {
  if (kind === 'unified') return LAYOUT_MIN.resultBlock.unified;
  if (kind === 'rail') return clamp(containerH * 0.12, LAYOUT_MIN.resultBlock.rail, 54);
  if (containerH <= 568) return clamp(containerH * 0.06, 32, 38);
  if (containerH < 620) return clamp(containerH * 0.055, 34, 42);
  return clamp(containerH * 0.05, LAYOUT_MIN.resultBlock.portrait, 44);
}

/** Pure height for the unified number selector row (pick + Lightning display). */
export function selectorRegionHeight(kind: CompositionKind, containerH: number): number {
  if (kind === 'unified') return LAYOUT_MIN.selector.unified;
  if (kind === 'rail') return clamp(containerH * 0.16, LAYOUT_MIN.selector.rail, 80);
  if (containerH <= 568) return clamp(containerH * 0.1, 52, 58);
  if (containerH < 620) return clamp(containerH * 0.095, 56, 64);
  return clamp(containerH * 0.1, LAYOUT_MIN.selector.portrait, 72);
}

/** Inter-row gap for a composition kind. */
export function regionGap(kind: CompositionKind, containerH: number): number {
  if (kind === 'unified') return 14;
  if (kind === 'rail') return clamp(containerH * 0.025, 10, 14);
  return clamp(containerH * 0.018, 10, 16);
}

/** Returns true when two axis-aligned rects overlap (positive area). */
export function rectsOverlap(a: Rect, b: Rect, tolerance = 0.5): boolean {
  const overlapW = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const overlapH = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return overlapW > tolerance && overlapH > tolerance;
}

/** Ordered gameplay bands for vertical-stack overlap tests (unified). */
export function orderedGameplayRegions(regions: SceneRegions): Rect[] {
  return [regions.topbar, regions.dice, regions.resultBlock, regions.selector, regions.stats, regions.controls];
}

/** Explicit portrait stack from topbar through roll button. */
export function orderedPortraitRegions(regions: SceneRegions): Rect[] {
  return [
    regions.topbar,
    regions.dice,
    regions.resultBlock,
    regions.selector,
    regions.balance,
    regions.betLastWin,
    regions.betControls,
    regions.rollButton,
  ];
}

/** Left play column bands for rail layout overlap tests. */
export function railPlayColumnRegions(regions: SceneRegions): Rect[] {
  return [regions.dice, regions.resultBlock, regions.selector];
}

/** Right control rail bands for rail layout overlap tests. */
export function railControlColumnRegions(regions: SceneRegions): Rect[] {
  return [regions.topbar, regions.stats, regions.controls];
}

/** Asserts stacked regions do not overlap vertically (same column). */
export function stackedRegionsSeparated(regions: Rect[], tolerance = 0.5): boolean {
  for (let i = 1; i < regions.length; i++) {
    const prev = regions[i - 1]!;
    const cur = regions[i]!;
    if (cur.y < prev.y + prev.h - tolerance) return false;
  }
  return true;
}

export interface DiceClusterLayout {
  dieSize: number;
  slotScale: number;
  centers: [number, number, number];
  cy: number;
  bounds: { left: number; right: number; top: number; bottom: number };
}

/** Mirrors scene dice placement — used by layout tests and DiceScene.layoutDice. */
export function diceClusterLayout(rect: Rect, baseDie = 100): DiceClusterLayout {
  const gap = Math.max(12, rect.w * 0.04);
  const dieSize = Math.min((rect.w - gap * 2) / 3, rect.h * 0.82);
  const totalW = dieSize * 3 + gap * 2;
  const left = rect.x + (rect.w - totalW) / 2;
  const cx0 = left + dieSize / 2;
  const cy = rect.y + rect.h / 2;
  return {
    dieSize,
    slotScale: dieSize / baseDie,
    centers: [cx0, cx0 + (dieSize + gap), cx0 + 2 * (dieSize + gap)],
    cy,
    bounds: { left, right: left + totalW, top: cy - dieSize / 2, bottom: cy + dieSize / 2 },
  };
}

/** Horizontal inset for portrait dice so edge dice stay inside the viewport. */
export function portraitDiceInsetX(innerW: number): number {
  if (innerW >= 390) return clamp(innerW * 0.045, 14, 22);
  return clamp(innerW * 0.035, 10, 16);
}

export interface OverlayLayoutBounds {
  resultBlock: { left: number; top: number; width: number; height: number };
  numberSelector: { left: number; top: number; width: number; height: number };
  lightningImpact: { left: number; top: number; width: number; height: number };
  errorToast: { left: number; top: number; width: number; height: number };
}

export interface SceneRegions {
  /** Visible safe rect in renderer coords. */
  safe: Rect;
  topbar: Rect;
  selector: Rect;
  dice: Rect;
  /** Compact round result (DOM overlay). */
  resultBlock: Rect;
  /** Floating error toast safe band. */
  errorToast: Rect;
  stats: Rect;
  controls: Rect;
  balance: Rect;
  betLastWin: Rect;
  betControls: Rect;
  rollButton: Rect;
}

export interface SceneLayout {
  mode: LayoutMode;
  kind: CompositionKind;
  reducedEffects: boolean;
  /** Unified transform of the virtual STAGE into the viewport. */
  stage: { scale: number; x: number; y: number };
  regions: SceneRegions;
  overlayCenter: { x: number; y: number };
}

export function dprCap(mode: LayoutMode, deviceDpr = 1): number {
  return shellDprCap(mode, deviceDpr);
}

export interface ToolbarSlots {
  /** Uniform button size (square) for every toolbar control. */
  size: number;
  /** Top-left X of each button, laid out right-aligned within `availW`. */
  xs: number[];
  /** Total pixel width consumed by the toolbar (buttons + gaps). */
  totalW: number;
  gap: number;
}

export function toolbarSlots(availW: number, availH: number, count: number, gap = 10): ToolbarSlots {
  const widthCap = Math.floor((availW - gap * (count - 1)) / count);
  const size = Math.max(28, Math.min(56, Math.floor(availH), widthCap));
  const totalW = size * count + gap * (count - 1);
  const x0 = Math.max(0, availW - totalW);
  const xs = Array.from({ length: count }, (_, i) => x0 + i * (size + gap));
  return { size, xs, totalW, gap };
}

export interface Slot {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ControlSlots {
  betDown: Slot;
  betUp: Slot;
  max: Slot;
  roll: Slot;
}

export function portraitBetLastWinLayout(rect: Rect): [StatSlot, StatSlot] {
  const gap = Math.max(8, rect.w * 0.025);
  const halfW = (rect.w - gap) / 2;
  return [
    { x: rect.x, y: rect.y, w: halfW, h: rect.h, variant: 'secondary' },
    { x: rect.x + halfW + gap, y: rect.y, w: halfW, h: rect.h, variant: 'secondary' },
  ];
}

export function portraitBetControlsLayout(rect: Rect): Pick<ControlSlots, 'betDown' | 'betUp' | 'max'> {
  const gap = Math.max(8, rect.w * 0.02);
  const colW = (rect.w - gap * 2) / 3;
  const y = rect.y + (rect.h - Math.min(rect.h, TOUCH_TARGET_MIN)) / 2;
  const h = Math.min(rect.h, Math.max(TOUCH_TARGET_MIN, rect.h));
  let x = rect.x;
  const betDown: Slot = { x, y, w: colW, h };
  x += colW + gap;
  const betUp: Slot = { x, y, w: colW, h };
  x += colW + gap;
  const max: Slot = { x, y, w: colW, h };
  return { betDown, betUp, max };
}

export function portraitRollLayout(rect: Rect): Slot {
  const h = Math.max(TOUCH_TARGET_MIN, rect.h);
  const y = rect.y + (rect.h - h) / 2;
  return { x: rect.x, y, w: rect.w, h };
}

export function controlsLayout(rect: Rect, kind: CompositionKind): ControlSlots {
  if (kind === 'unified') {
    const gap = Math.max(12, rect.w * 0.015);
    const h = Math.min(rect.h, 72);
    const y = rect.y + (rect.h - h) / 2;
    const stepW = Math.max(56, h);
    const maxW = Math.max(84, h * 1.4);
    const rollW = Math.max(160, rect.w - (stepW * 2 + maxW + gap * 4));
    let x = rect.x;
    const betDown: Slot = { x, y, w: stepW, h };
    x += stepW + gap;
    const betUp: Slot = { x, y, w: stepW, h };
    x += stepW + gap;
    const max: Slot = { x, y, w: maxW, h };
    x += maxW + gap;
    const roll: Slot = { x, y, w: rollW, h };
    return { betDown, betUp, max, roll };
  }
  const gap = Math.max(10, rect.h * 0.05);
  const rowH = Math.max(44, (rect.h - gap) / 2);
  const colGap = Math.max(8, rect.w * 0.02);
  const colW = (rect.w - colGap * 2) / 3;
  const y0 = rect.y;
  let x = rect.x;
  const betDown: Slot = { x, y: y0, w: colW, h: rowH };
  x += colW + colGap;
  const betUp: Slot = { x, y: y0, w: colW, h: rowH };
  x += colW + colGap;
  const max: Slot = { x, y: y0, w: colW, h: rowH };
  const roll: Slot = { x: rect.x, y: y0 + rowH + gap, w: rect.w, h: rowH };
  return { betDown, betUp, max, roll };
}

export type StatCardVariant = 'primary' | 'secondary' | 'default';

export interface StatSlot extends Slot {
  variant: StatCardVariant;
}

export function statsLayout(rect: Rect, kind: CompositionKind): [StatSlot, StatSlot, StatSlot] {
  if (kind === 'portrait') {
    throw new Error('portrait uses portraitBalanceLayout / portraitBetLastWinLayout');
  }
  if (kind === 'rail') {
    const gap = Math.max(8, rect.h * 0.06);
    const cardH = (rect.h - gap * 2) / 3;
    return [0, 1, 2].map((i) => ({
      x: rect.x,
      y: rect.y + i * (cardH + gap),
      w: rect.w,
      h: cardH,
      variant: 'default' as const,
    })) as [StatSlot, StatSlot, StatSlot];
  }
  const gap = Math.max(10, rect.w * 0.02);
  const cardW = (rect.w - gap * 2) / 3;
  const cardH = Math.min(rect.h, 92);
  const y = rect.y + (rect.h - cardH) / 2;
  return [0, 1, 2].map((i) => ({
    x: rect.x + i * (cardW + gap),
    y,
    w: cardW,
    h: cardH,
    variant: 'default' as const,
  })) as [StatSlot, StatSlot, StatSlot];
}

export function portraitBalanceLayout(rect: Rect): StatSlot {
  return { x: rect.x, y: rect.y, w: rect.w, h: rect.h, variant: 'primary' };
}

export const STAT_VALUE_FONT_MIN = { primary: 20, secondary: 16, default: 14 } as const;

export function computeSceneLayout(vp: Viewport, insets: Insets): SceneLayout {
  const mode = classifyLayout(vp);
  const kind = compositionKind(mode);
  const reduced = shellReducedEffects(mode);
  const safe = safeRect(vp, insets);

  if (kind === 'unified') {
    return unifiedLayout(vp, mode, reduced);
  }
  if (kind === 'rail') {
    return railLayout(mode, reduced, safe);
  }
  return portraitLayout(mode, reduced, safe);
}

function unifiedLayout(vp: Viewport, mode: LayoutMode, reduced: boolean): SceneLayout {
  const scale = Math.min(vp.width / STAGE.width, vp.height / STAGE.height);
  const x = (vp.width - STAGE.width * scale) / 2;
  const y = (vp.height - STAGE.height * scale) / 2;
  const W = STAGE.width;
  const marginX = 40;
  const contentW = W - marginX * 2;
  const gap = regionGap('unified', STAGE.height);
  const marginTop = 20;
  const marginBottom = 16;

  const topbarH = 80;
  const selectorH = selectorRegionHeight('unified', STAGE.height);
  const resultH = resultBlockHeight('unified', STAGE.height);
  const statsH = 88;
  const controlsH = 72;
  const contentH = STAGE.height - marginTop - marginBottom;
  const totalGaps = gap * 5;
  const rowMins = [topbarH, LAYOUT_MIN.dice.unified, resultH, selectorH, statsH, controlsH];
  const rowHs = fitStackHeights(contentH, totalGaps, rowMins, 1);

  let rowY = marginTop;
  const row = (h: number, afterGap = gap): Rect => {
    const r: Rect = { x: marginX, y: rowY, w: contentW, h };
    rowY += h + afterGap;
    return r;
  };

  const topbar = row(rowHs[0]!);
  const dice: Rect = { x: 160, y: rowY, w: W - 320, h: rowHs[1]! };
  rowY += rowHs[1]! + gap;
  const resultBlock = row(rowHs[2]!);
  const selector = row(rowHs[3]!);
  const stats = row(rowHs[4]!);
  const controls = row(rowHs[5]!);

  const errorToast: Rect = {
    x: marginX + contentW * 0.12,
    y: topbar.y + topbar.h - LAYOUT_MIN.errorToast - 4,
    w: contentW * 0.76,
    h: LAYOUT_MIN.errorToast,
  };

  const regions: SceneRegions = {
    safe: { x: 0, y: 0, w: W, h: STAGE.height },
    topbar,
    selector,
    dice,
    resultBlock,
    errorToast,
    stats,
    controls,
    balance: stats,
    betLastWin: stats,
    betControls: controls,
    rollButton: controls,
  };
  return {
    mode,
    kind: 'unified',
    reducedEffects: reduced,
    stage: { scale, x, y },
    regions,
    overlayCenter: { x: vp.width / 2, y: vp.height / 2 },
  };
}

function portraitLayout(mode: LayoutMode, reduced: boolean, safe: Rect): SceneLayout {
  const inner = padRect(safe, clamp(safe.w * 0.025, 10, 20), clamp(safe.h * 0.018, 8, 16));
  const ultra = inner.h <= 568;
  const compact = inner.h < 620;
  const tiny = inner.h < 700;
  const gap = ultra ? 6 : tiny ? 8 : regionGap('portrait', inner.h);
  const bottomInset = ultra ? 6 : LAYOUT_MIN.bottomInset.portrait;
  const playableH = inner.h - bottomInset;

  const mins = {
    topbar: ultra ? 44 : tiny ? 48 : clamp(inner.h * 0.08, 52, 72),
    dice: ultra ? 76 : compact ? 84 : clamp(inner.h * 0.22, LAYOUT_MIN.dice.portrait, 120),
    result: resultBlockHeight('portrait', inner.h),
    selector: selectorRegionHeight('portrait', inner.h),
    balance: ultra ? 34 : LAYOUT_MIN.balanceRow.portrait,
    betLastWin: ultra ? 34 : LAYOUT_MIN.betLastWinRow.portrait,
    betControls: TOUCH_TARGET_MIN,
    roll: ultra ? LAYOUT_MIN.rollRow.portrait : clamp(inner.h * 0.075, 48, 56),
  };

  const rowMins = [
    mins.topbar,
    mins.dice,
    mins.result,
    mins.selector,
    mins.balance,
    mins.betLastWin,
    mins.betControls,
    mins.roll,
  ];
  const totalGaps = gap * (rowMins.length - 1);
  const rowHs = fitStackHeights(playableH, totalGaps, rowMins, 1);

  let y = inner.y;
  const region = (h: number, afterGap = gap): Rect => {
    const r: Rect = { x: inner.x, y, w: inner.w, h };
    y += h + afterGap;
    return r;
  };

  const topbar = region(rowHs[0]!);
  const diceInsetX = portraitDiceInsetX(inner.w);
  const dice: Rect = {
    x: inner.x + diceInsetX,
    y,
    w: inner.w - diceInsetX * 2,
    h: rowHs[1]!,
  };
  y += rowHs[1]! + gap;
  const resultBlock = region(rowHs[2]!);
  const selector = region(rowHs[3]!);
  const balance = region(rowHs[4]!);
  const betLastWin = region(rowHs[5]!);
  const betControls = region(rowHs[6]!);
  const rollButton = region(rowHs[7]!, 0);

  const stats: Rect = {
    x: balance.x,
    y: balance.y,
    w: balance.w,
    h: betLastWin.y + betLastWin.h - balance.y,
  };
  const controls: Rect = {
    x: betControls.x,
    y: betControls.y,
    w: betControls.w,
    h: rollButton.y + rollButton.h - betControls.y,
  };

  const errorToast: Rect = {
    x: inner.x + inner.w * 0.06,
    y: topbar.y + topbar.h - LAYOUT_MIN.errorToast - 2,
    w: inner.w * 0.88,
    h: LAYOUT_MIN.errorToast,
  };

  return {
    mode,
    kind: 'portrait',
    reducedEffects: reduced,
    stage: { scale: 1, x: 0, y: 0 },
    regions: {
      safe: inner,
      topbar,
      selector,
      dice,
      resultBlock,
      errorToast,
      stats,
      controls,
      balance,
      betLastWin,
      betControls,
      rollButton,
    },
    overlayCenter: { x: safe.x + safe.w / 2, y: safe.y + safe.h / 2 },
  };
}

function railLayout(mode: LayoutMode, reduced: boolean, safe: Rect): SceneLayout {
  const inner = padRect(safe, clamp(safe.w * 0.022, 10, 20), clamp(safe.h * 0.028, 10, 18));
  const railGap = clamp(inner.w * 0.028, 12, 24);
  const railW = clamp(inner.w * 0.32, 220, 300);
  const mainW = inner.w - railW - railGap;
  const railX = inner.x + mainW + railGap;
  const gap = regionGap('rail', inner.h);
  const tiny = inner.h < 340;

  const leftMins = [
    LAYOUT_MIN.dice.rail,
    resultBlockHeight('rail', inner.h),
    selectorRegionHeight('rail', inner.h),
  ];
  const leftHs = fitStackHeights(inner.h, gap * 2, leftMins, 0);

  let leftY = inner.y;
  const leftRow = (h: number, afterGap = gap): Rect => {
    const r: Rect = { x: inner.x, y: leftY, w: mainW, h };
    leftY += h + afterGap;
    return r;
  };

  const dice = leftRow(leftHs[0]!);
  const resultBlock = leftRow(leftHs[1]!);
  const selector = leftRow(leftHs[2]!);

  const topbarH = tiny ? 44 : clamp(inner.h * 0.13, 48, 68);
  const statsH = tiny ? 88 : clamp(inner.h * 0.36, 96, 168);
  const topbar: Rect = { x: railX, y: inner.y, w: railW, h: topbarH };
  const stats: Rect = { x: railX, y: inner.y + topbarH + gap, w: railW, h: statsH };
  const controls: Rect = {
    x: railX,
    y: stats.y + stats.h + gap,
    w: railW,
    h: Math.max(88, inner.y + inner.h - (stats.y + stats.h + gap)),
  };

  const errorToast: Rect = {
    x: railX,
    y: controls.y + controls.h - LAYOUT_MIN.errorToast - 6,
    w: railW,
    h: LAYOUT_MIN.errorToast,
  };

  return {
    mode,
    kind: 'rail',
    reducedEffects: reduced,
    stage: { scale: 1, x: 0, y: 0 },
    regions: {
      safe: inner,
      topbar,
      selector,
      dice,
      resultBlock,
      errorToast,
      stats,
      controls,
      balance: stats,
      betLastWin: stats,
      betControls: controls,
      rollButton: controls,
    },
    overlayCenter: { x: inner.x + mainW / 2, y: safe.y + safe.h / 2 },
  };
}

/** Distributes vertical space across stacked rows; flexIndex receives leftover height. */
function fitStackHeights(containerH: number, totalGaps: number, mins: number[], flexIndex: number): number[] {
  const minSum = mins.reduce((sum, h) => sum + h, 0);
  if (minSum + totalGaps <= containerH) {
    const extra = containerH - minSum - totalGaps;
    return mins.map((h, i) => (i === flexIndex ? h + extra : h));
  }
  const scale = (containerH - totalGaps) / minSum;
  return mins.map((h) => Math.max(8, Math.floor(h * scale)));
}

function padRect(r: Rect, gx: number, gy: number): Rect {
  return { x: r.x + gx, y: r.y + gy, w: Math.max(1, r.w - gx * 2), h: Math.max(1, r.h - gy * 2) };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
